import { getSellingPriceByDomain } from 'hooks/formatting/useFormatPrice';
import { useDomainConfig } from 'hooks/useDomainConfig';
import Head from 'next/head';
import { ProductDetailType } from 'types/product';
import { REVIEW_RATING_DEFAULT_MAX } from 'types/review';

type ProductMetadataProps = {
    product: ProductDetailType;
};

export const ProductMetadata: FC<ProductMetadataProps> = ({ product }) => {
    const domainConfig = useDomainConfig();
    const imageUrls = product.images;
    const { url } = useDomainConfig();
    const slugWithoutLeadingSlash = product.slug.charAt(0) === '/' ? product.slug.slice(1) : product.slug;
    const absoluteUrl = url + slugWithoutLeadingSlash;
    const productDescription = product.description
        .replace(/<\/(p|h[1-6]|br)>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();

    const reviews =
        product.reviews.length > 0
            ? product.reviews.map((review) => ({
                  '@type': 'Review',
                  reviewRating: {
                      '@type': 'Rating',
                      ratingValue: parseFloat(review.rating.toFixed(2)),
                      bestRating: REVIEW_RATING_DEFAULT_MAX,
                  },
                  author: {
                      '@type': 'Person',
                      name: review.authorOrCustomerNameAnonymized,
                  },
              }))
            : undefined;

    return (
        <Head>
            <script
                type="application/ld+json"
                id="product-metadata"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify([
                        {
                            '@context': 'https://schema.org/',
                            '@type': 'Product',
                            name: product.fullName,
                            image: imageUrls,
                            description: productDescription,
                            sku: product.catalogNumber,
                            mpn: product.ean,
                            ...(reviews ? { review: reviews } : {}),
                            aggregateRating: {
                                '@type': 'AggregateRating',
                                ratingValue: parseFloat(product.reviewsSummary.averageRating.toFixed(2)),
                                reviewCount: product.reviewsSummary.totalCount,
                            },
                            brand: {
                                '@type': 'Brand',
                                name: product.brand?.name,
                            },
                            offers: {
                                '@type': 'Offer',
                                url: absoluteUrl,
                                priceCurrency: product.price.currencyCode,
                                price: getSellingPriceByDomain(product.price, domainConfig),
                                itemCondition: 'https://schema.org/NewCondition',
                                availability:
                                    product.availability.status === 'in-stock'
                                        ? 'https://schema.org/InStock'
                                        : 'https://schema.org/OutOfStock',
                            },
                        },
                    ]),
                }}
                key="product-metadata"
            />
        </Head>
    );
};
